import { loadSettings } from "../storage.ts";

export type GatewaySttCallbacks = {
  onTranscript: (text: string, isFinal: boolean) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
};

let activeRecorder: MediaRecorder | null = null;
let activeStream: MediaStream | null = null;
let activeChunks: Blob[] = [];
let activeCallbacks: GatewaySttCallbacks | null = null;

type AudioInputDevice = MediaDeviceInfo & { kind: "audioinput" };

function stopTracks() {
  if (activeStream) {
    for (const track of activeStream.getTracks()) {
      try {
        track.stop();
      } catch {
        // ignore
      }
    }
    activeStream = null;
  }
}

function bestMimeType(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  for (const candidate of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return "audio/webm";
}

function resolveGatewayAuthHeaders(): Record<string, string> {
  try {
    const settings = loadSettings();
    const token = settings.token.trim();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
  } catch {
    // ignore
  }
  return {};
}

function describeUnknownError(err: unknown): string {
  if (typeof err === "string") {
    return err;
  }
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object") {
    const record = err as Record<string, unknown>;
    const directMessage = typeof record.message === "string" ? record.message.trim() : "";
    if (directMessage) {
      return directMessage;
    }
    const nestedError = record.error;
    if (typeof nestedError === "string" && nestedError.trim()) {
      return nestedError.trim();
    }
    if (nestedError && typeof nestedError === "object") {
      const nestedRecord = nestedError as Record<string, unknown>;
      const nestedMessage =
        typeof nestedRecord.message === "string" ? nestedRecord.message.trim() : "";
      if (nestedMessage) {
        return nestedMessage;
      }
      const nestedType = typeof nestedRecord.type === "string" ? nestedRecord.type.trim() : "";
      if (nestedType) {
        return nestedType;
      }
    }
    try {
      return JSON.stringify(err);
    } catch {
      // ignore
    }
  }
  return String(err);
}

function normalizeError(err: unknown): string {
  const message = describeUnknownError(err);
  const lower = message.toLowerCase();
  if (lower.includes("requested device not found") || lower.includes("notfounderror")) {
    return "No microphone device was found";
  }
  if (
    lower.includes("permission denied") ||
    lower.includes("notallowederror") ||
    lower.includes("permissiondismissederror")
  ) {
    return "Microphone permission denied for this page";
  }
  if (lower.includes("notreadableerror") || lower.includes("trackstarterror")) {
    return "Microphone is busy or unavailable";
  }
  if (lower.includes("overconstrainederror") || lower.includes("constraint")) {
    return "Selected microphone could not be opened";
  }
  return message;
}

async function listAudioInputs(): Promise<AudioInputDevice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d): d is AudioInputDevice => d.kind === "audioinput");
}

function pickPreferredDevice(devices: AudioInputDevice[]): AudioInputDevice | null {
  if (devices.length === 0) {
    return null;
  }
  const preferred = devices.find((d) => /default|communications|realtek|microphone/i.test(d.label));
  return preferred ?? devices[0] ?? null;
}

async function openAudioStream(): Promise<MediaStream> {
  const attempts: Array<MediaStreamConstraints> = [];

  // First try a permissive request to trigger prompt / grant.
  attempts.push({ audio: true });

  let devices: AudioInputDevice[] = [];
  try {
    devices = await listAudioInputs();
  } catch {
    // ignore enumerate failure before permission grant
  }

  const preferred = pickPreferredDevice(devices);
  if (preferred?.deviceId) {
    attempts.push({
      audio: {
        deviceId: { ideal: preferred.deviceId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    attempts.push({ audio: { deviceId: { exact: preferred.deviceId } } });
  }

  // Generic constrained fallback after device-specific attempts.
  attempts.push({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  attempts.push({ audio: true });

  let lastError: unknown = new Error("Unable to access microphone");
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastError = err;
      continue;
    }
  }

  // One more enumerate after failure: if still no inputs, return clearer error.
  try {
    const refreshed = await listAudioInputs();
    if (refreshed.length === 0) {
      throw new Error("No microphone device was found");
    }
  } catch (err) {
    lastError = err;
  }
  throw lastError;
}

export function isGatewaySttSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!navigator.mediaDevices?.enumerateDevices &&
    typeof MediaRecorder !== "undefined" &&
    typeof fetch !== "undefined"
  );
}

export async function startGatewayStt(callbacks: GatewaySttCallbacks): Promise<boolean> {
  if (!isGatewaySttSupported()) {
    callbacks.onError?.("Audio recording is not supported in this browser");
    return false;
  }

  stopGatewayStt();

  try {
    const stream = await openAudioStream();
    const recorder = new MediaRecorder(stream, { mimeType: bestMimeType() });
    activeStream = stream;
    activeRecorder = recorder;
    activeChunks = [];
    activeCallbacks = callbacks;

    recorder.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) {
        activeChunks.push(event.data);
      }
    });

    recorder.addEventListener("start", () => callbacks.onStart?.());

    recorder.addEventListener("stop", async () => {
      const chunks = activeChunks.slice();
      const mimeType = recorder.mimeType || "audio/webm";
      activeChunks = [];
      activeRecorder = null;
      stopTracks();
      try {
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size === 0) {
          callbacks.onError?.("No audio captured");
          callbacks.onEnd?.();
          return;
        }
        const res = await fetch("/api/control-ui/stt", {
          method: "POST",
          headers: {
            "Content-Type": mimeType,
            ...resolveGatewayAuthHeaders(),
          },
          body: blob,
          credentials: "same-origin",
        });
        const json = (await res.json().catch(() => null)) as
          | { ok?: boolean; text?: string; error?: string }
          | null;
        if (!res.ok || !json?.ok) {
          callbacks.onError?.(json?.error || `STT request failed (${res.status})`);
          callbacks.onEnd?.();
          return;
        }
        const text = typeof json.text === "string" ? json.text.trim() : "";
        if (text) {
          callbacks.onTranscript(text, true);
        }
        callbacks.onEnd?.();
      } catch (err) {
        callbacks.onError?.(normalizeError(err));
        callbacks.onEnd?.();
      } finally {
        activeCallbacks = null;
      }
    });

    recorder.addEventListener("error", (event) => {
      callbacks.onError?.((event as ErrorEvent).message || "Audio recording failed");
    });

    recorder.start();
    return true;
  } catch (err) {
    stopTracks();
    activeRecorder = null;
    activeChunks = [];
    activeCallbacks = null;
    callbacks.onError?.(normalizeError(err));
    return false;
  }
}

export function stopGatewayStt(): void {
  if (activeRecorder && activeRecorder.state !== "inactive") {
    try {
      activeRecorder.stop();
    } catch {
      // ignore
    }
  } else {
    stopTracks();
    activeRecorder = null;
    activeChunks = [];
    activeCallbacks?.onEnd?.();
    activeCallbacks = null;
  }
}

export function isGatewaySttActive(): boolean {
  return !!activeRecorder && activeRecorder.state !== "inactive";
}
